import { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import WeaponeeringAPI from '../services/WeaponeeringAPIClient';

/**
 * Custom hook for Targeting Workspace management
 *
 * Manages analyst workspace with:
 * - Priority requests (high-priority items flagged by planners)
 * - Pinned items (analyst favorites)
 * - Recently worked items (auto-tracked by activity)
 *
 * Features:
 * - Automatic workspace loading on mount
 * - Activity tracking (debounced on backend)
 * - Pin/unpin management
 * - Priority acknowledgment
 * - Notes management
 *
 * @param {string|null} analystId - Analyst email (from Auth0)
 * @returns {Object} Workspace state and actions
 */

/**
 * Fetch the analyst workspace, retrying once (after a 500ms wait) if the
 * token isn't ready yet, and again (once) if the response comes back
 * 401/403.
 *
 * React fires child effects before parent effects, so this hook's
 * mount-time load can fire before useApi()'s effect (in the parent App
 * component) has called WeaponeeringAPI.setTokenProvider() -- resolveToken()
 * then has nothing to call and, on a cold client with no primed cache (e.g.
 * a fresh Playwright context), returns null. Short-circuiting on a null
 * token rather than firing a request that's guaranteed to fail matters
 * because the backend's HTTPBearer(auto_error=True) rejects a missing
 * Authorization header with 403, not 401, so waiting for a response to
 * retry-on-401 alone would miss this exact case. The second retry (on a
 * 401/403 response) is belt-and-suspenders for the same race if a
 * stale/invalid token made it through the first check.
 *
 * Exported standalone (no hook/state dependencies -- fetch, token resolver,
 * and the wait itself are all injected) so the retry sequencing is
 * unit-testable without mounting the hook.
 */
export async function fetchWorkspaceWithRetry({
  analystId,
  apiBaseUrl,
  resolveToken,
  fetchImpl,
  wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms)),
  isRetry = false,
}) {
  const token = await resolveToken();

  if (!token && !isRetry) {
    await wait(500);
    return fetchWorkspaceWithRetry({ analystId, apiBaseUrl, resolveToken, fetchImpl, wait, isRetry: true });
  }

  const response = await fetchImpl(
    `${apiBaseUrl}/api/weaponeering/workspace/${encodeURIComponent(analystId)}`,
    { headers: token ? { Authorization: `Bearer ${token}` } : {} }
  );

  if (!response.ok) {
    if ((response.status === 401 || response.status === 403) && !isRetry) {
      await wait(500);
      return fetchWorkspaceWithRetry({ analystId, apiBaseUrl, resolveToken, fetchImpl, wait, isRetry: true });
    }
    throw new Error(`Failed to load workspace: ${response.statusText}`);
  }

  return response.json();
}

export function useTargetingWorkspace(analystId) {
  const [workspace, setWorkspace] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000';

  /**
   * Load workspace data for analyst
   */
  const loadWorkspace = useCallback(async () => {
    if (!analystId) {
      setWorkspace(null);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const data = await fetchWorkspaceWithRetry({
        analystId,
        apiBaseUrl: API_BASE_URL,
        resolveToken: WeaponeeringAPI.resolveToken.bind(WeaponeeringAPI),
        fetchImpl: fetch,
      });
      setWorkspace(data);
    } catch (err) {
      console.error('Workspace load error:', err);
      setError(err.message);
      setWorkspace(null);
    } finally {
      setLoading(false);
    }
  }, [analystId, API_BASE_URL]);

  /**
   * Load workspace on mount and when analystId changes
   */
  useEffect(() => {
    loadWorkspace();
  }, [loadWorkspace]);

  /**
   * Track activity (entity view)
   * Fire-and-forget - doesn't block UI
   */
  const trackActivity = useCallback(
    async (entityType, entityId) => {
      if (!analystId) return;

      try {
        const token = await WeaponeeringAPI.resolveToken();
        await fetch(`${API_BASE_URL}/api/weaponeering/workspace/activity/track`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({
            analyst_id: analystId,
            entity_type: entityType,
            entity_id: entityId,
          }),
        });
        // Refresh workspace to show updated activity
        loadWorkspace();
      } catch (err) {
        console.warn('Activity tracking failed (non-critical):', err);
      }
    },
    [analystId, API_BASE_URL, loadWorkspace]
  );

  /**
   * Toggle pin status for workspace item
   */
  const togglePin = useCallback(
    async (itemId, currentPinStatus) => {
      if (!analystId) return;

      try {
        const token = await WeaponeeringAPI.resolveToken();
        const response = await fetch(
          `${API_BASE_URL}/api/weaponeering/workspace/items/${itemId}`,
          {
            method: 'PATCH',
            headers: {
              'Content-Type': 'application/json',
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
            body: JSON.stringify({
              pinned: !currentPinStatus,
            }),
          }
        );

        if (!response.ok) {
          throw new Error(`Failed to toggle pin: ${response.statusText}`);
        }

        // Refresh workspace
        loadWorkspace();
      } catch (err) {
        console.error('Toggle pin error:', err);
        setError(err.message);
      }
    },
    [analystId, API_BASE_URL, loadWorkspace]
  );

  /**
   * Acknowledge priority request
   */
  const acknowledgePriority = useCallback(
    async (itemId) => {
      if (!analystId) return;

      try {
        const token = await WeaponeeringAPI.resolveToken();
        const response = await fetch(
          `${API_BASE_URL}/api/weaponeering/workspace/items/${itemId}`,
          {
            method: 'PATCH',
            headers: {
              'Content-Type': 'application/json',
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
            body: JSON.stringify({
              priority_acknowledged: true,
            }),
          }
        );

        if (!response.ok) {
          throw new Error(`Failed to acknowledge priority: ${response.statusText}`);
        }

        // Refresh workspace
        loadWorkspace();
      } catch (err) {
        console.error('Acknowledge priority error:', err);
        setError(err.message);
      }
    },
    [analystId, API_BASE_URL, loadWorkspace]
  );

  /**
   * Update notes for workspace item
   */
  const updateNotes = useCallback(
    async (itemId, notes) => {
      if (!analystId) return;

      try {
        const token = await WeaponeeringAPI.resolveToken();
        const response = await fetch(
          `${API_BASE_URL}/api/weaponeering/workspace/items/${itemId}`,
          {
            method: 'PATCH',
            headers: {
              'Content-Type': 'application/json',
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
            body: JSON.stringify({
              analyst_notes: notes,
            }),
          }
        );

        if (!response.ok) {
          throw new Error(`Failed to update notes: ${response.statusText}`);
        }

        // Refresh workspace
        loadWorkspace();
      } catch (err) {
        console.error('Update notes error:', err);
        setError(err.message);
      }
    },
    [analystId, API_BASE_URL, loadWorkspace]
  );

  /**
   * Remove item from workspace
   */
  const removeFromWorkspace = useCallback(
    async (itemId) => {
      if (!analystId) return;

      try {
        const token = await WeaponeeringAPI.resolveToken();
        const response = await fetch(
          `${API_BASE_URL}/api/weaponeering/workspace/items/${itemId}`,
          {
            method: 'DELETE',
            headers: token ? { Authorization: `Bearer ${token}` } : {},
          }
        );

        if (!response.ok) {
          throw new Error(`Failed to remove item: ${response.statusText}`);
        }

        // Refresh workspace
        loadWorkspace();
      } catch (err) {
        console.error('Remove from workspace error:', err);
        setError(err.message);
      }
    },
    [analystId, API_BASE_URL, loadWorkspace]
  );

  /**
   * Manually refresh workspace
   */
  const refresh = useCallback(() => {
    loadWorkspace();
  }, [loadWorkspace]);

  return {
    // Workspace state
    workspace, // { priority_items: [], pinned_items: [], recent_items: [] }
    loading,
    error,

    // Actions
    trackActivity,
    togglePin,
    acknowledgePriority,
    updateNotes,
    removeFromWorkspace,
    refresh,
  };
}

/**
 * PropTypes for workspace item objects
 */
export const WorkspaceItemPropType = PropTypes.shape({
  id: PropTypes.string.isRequired,
  analyst_id: PropTypes.string.isRequired,
  entity_type: PropTypes.oneOf(['site', 'facility', 'dmpi']).isRequired,
  entity_id: PropTypes.string.isRequired,
  entity_name: PropTypes.string,
  short_code: PropTypes.string,
  last_accessed_at: PropTypes.string,
  access_count: PropTypes.number,
  priority_level: PropTypes.number,
  priority_requested_by: PropTypes.string,
  priority_requested_at: PropTypes.string,
  priority_acknowledged: PropTypes.bool,
  pinned: PropTypes.bool,
  analyst_notes: PropTypes.string,
  // Lineage
  site_name: PropTypes.string,
  facility_name: PropTypes.string,
  dmpi_name: PropTypes.string,
});

export default useTargetingWorkspace;
